import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHorizRounded";
import { IconButton } from "@mui/material";
import { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import {
  getEditApiReportUrl,
  getEditSystemReportUrl,
  getEditUiReportUrl,
  getViewApiReportUrl,
  getViewSystemReportUrl,
  getViewUiReportUrl,
} from "../utilities/editReportUrl";
import ReportActions from "./ReportActions";

interface Props<T extends ReportInfo> {
  report: T;
  openInNewTab?: boolean;
  onReportSelected: (report?: T) => void;
}

export default function ReportMenuAction<T extends ReportInfo>({ report, openInNewTab, onReportSelected }: Props<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleViewReport = useCallback(() => {
    switch (report.reportUsage) {
      case "Ui":
        return getViewUiReportUrl(report.clientCode, report.reportId);
      case "Api":
        return getViewApiReportUrl(report.clientCode, report.reportId);
      case "System":
        return getViewSystemReportUrl(report.clientCode, report.reportId);
      default:
        throw new Error(`Unsupported report usage: ${report.reportUsage}`);
    }
  }, [report]);

  const handleEditReport = useCallback(() => {
    switch (report.reportUsage) {
      case "Ui":
        return getEditUiReportUrl(report.clientCode, report.reportId);
      case "Api":
        return getEditApiReportUrl(report.clientCode, report.reportId);
      case "System":
        return getEditSystemReportUrl(report.clientCode, report.reportId);
      default:
        throw new Error(`Unsupported report usage: ${report.reportUsage}`);
    }
  }, [report]);

  const handleClose = useCallback(() => {
    onReportSelected(undefined);
    setAnchorEl(null);
  }, [onReportSelected]);

  return (
    <>
      <ReportAuthorizedBox
        clientCode={report.clientCode}
        permissions={["ManageReports"]}
        requiredAccess={ReportAccessType.Write}
        access={report.authorization.access}
      >
        <IconButton component={RouterLink} to={handleEditReport()} target={openInNewTab ? "_blank" : undefined}>
          <DriveFileRenameOutlineOutlinedIcon />
        </IconButton>
      </ReportAuthorizedBox>
      <IconButton
        onClick={(evt) => {
          setAnchorEl(evt.currentTarget);
          onReportSelected(report);
        }}
        color="secondary"
      >
        <MoreHorizIcon />
      </IconButton>
      <ReportActions
        open={!!anchorEl}
        anchorEl={anchorEl}
        report={report}
        onRun={handleViewReport}
        onEdit={handleEditReport}
        onClose={handleClose}
        openInNewTab={openInNewTab}
      />
    </>
  );
}
